import { AsyncPipe, CommonModule, isPlatformBrowser } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	effect,
	EventEmitter,
	inject,
	Input,
	input,
	model,
	OnInit,
	Output,
	output,
	PLATFORM_ID,
	signal,
} from '@angular/core';

import { TranslatePipe } from '@ngx-translate/core';
import dayjs from 'dayjs';
import {
	BehaviorSubject,
	distinctUntilChanged,
	filter,
	Observable,
	shareReplay,
	switchMap,
	tap,
} from 'rxjs';

import {
	AvailabilityDatesInterface,
	AvailabilityRequestInterface,
} from '@valk-nx/availability-dates-store/availability-dates.interface';
import { AvailabilityDatesService } from '@valk-nx/availability-dates-store/availability-dates.service';
import { TabMenuComponent } from '@valk-nx/components/ui-tab-menu/src/lib/tab-menu';
import { TabMenuItemDirective } from '@valk-nx/components/ui-tab-menu/src/lib/tab-menu.directive';
import { cn, fallbackLanguage } from '@valk-nx/core/lib/core';
import {
	HotelInterface,
	OccupancyRestrictionsInterface,
} from '@valk-nx/core/lib/interfaces/hotels.interface';
import { OccupancyInterface } from '@valk-nx/helpers/lib/interfaces/occupancy.interface';
import { toDate } from '@valk-nx/helpers/lib/transformers/toDate';
import { RouteHelper } from '@valk-nx/router-store/router.helper';

import { AvailabilityWidgetInputDateRangeComponent } from './availability-input-date-range/availability-input-date-range';
import { AvailabilityWidgetInputDropdownComponent } from './availability-input-dropdown/availability-input-dropdown';
import { AvailabilityInputDropdownItem } from './availability-input-dropdown/availability-input-dropdown.interface';
import { AvailabilityWidgetInputOccupancyComponent } from './availability-input-occupancy/availability-input-occupancy';
import {
	AccommodationTypeInterface,
	AvailabilityWidgetEmitInterface,
	WidgetDataActionInterface,
} from './availability-widget.component.interface';

export const occupancyRestrictions = {
	adults: {
		min: 1,
		max: 3,
	},
	children: {
		min: 0,
		max: 1,
	},
	infants: {
		min: 0,
		max: 1,
	},
	maxPersons: 2,
	maxOccupancies: 5,
};

const yesterday = dayjs().subtract(1, 'day').toDate();

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: `vp-availability-widget`,
	templateUrl: './availability-widget.component.html',
	imports: [
		AvailabilityWidgetInputDateRangeComponent,
		AvailabilityWidgetInputOccupancyComponent,
		AvailabilityWidgetInputDropdownComponent,
		AsyncPipe,
		CommonModule,
		TabMenuComponent,
		TabMenuItemDirective,
		TranslatePipe,
	],
})
export class AvailabilityWidgetComponent implements OnInit {
	private readonly availability = inject(AvailabilityDatesService);
	readonly platformId = inject(PLATFORM_ID);

	@Input() hotelGUID: string;
	hotelSlug = input<string | undefined>(undefined);
	@Input() roomGUID: string | undefined;
	@Input() dealGUID: string | undefined;
	packageGUID = input<string | undefined>(undefined);

	@Input() accommodationTypes: AccommodationTypeInterface[] | undefined;
	activeAccommodationType = signal(0);
	@Input() initialOccupancy: OccupancyInterface[];
	@Input() defaultAdults = 2;
	@Input() occupancyRestrictions: OccupancyRestrictionsInterface =
		occupancyRestrictions;

	@Input({ transform: toDate }) initialArrivalDate: Date;
	@Input({ transform: toDate }) initialDepartureDate: Date;
	@Input() minDate = yesterday;
	@Input() maxDate = dayjs().add(1, 'year').toDate();

	@Input() hotels: AvailabilityInputDropdownItem[] = [];
	@Input() stepperConfig = { useStepper: false, min: 1, max: undefined };
	@Input() useCustomEmitter = false;

	currency = input<string>('EUR');
	showHotels = input(false);
	showOccupancy = input(true);
	showDates = input(true);
	showPrices = input(true);
	showPackages = input(false);
	showAccommodationTypes = input(false);
	packages = input<AvailabilityInputDropdownItem[]>([]);
	forceHideRangePrices = input<boolean>(false);

	openAvailabilityInNewTab = input<boolean>(false);

	@Output() customEmitter: EventEmitter<AvailabilityWidgetEmitInterface> =
		new EventEmitter<AvailabilityWidgetEmitInterface>();
	actionClick = output<{
		event: string;
		eventType: string;
		data: WidgetDataActionInterface;
	}>();
	packageSelected = output<number>();
	occupancyChanged = output<OccupancyInterface[]>();

	@Input({ required: true }) bookingtoolUrl: string;
	@Input({ required: true }) language: string = fallbackLanguage;

	occupancy: OccupancyInterface[];

	updateAvailability$ = new BehaviorSubject<AvailabilityRequestInterface>(
		undefined,
	);
	isLoading = false;
	jumpToFirstAvailableDate = true;

	availability$: Observable<AvailabilityDatesInterface>;
	firstDateInCalendar: string = dayjs().format('YYYY-MM-DD');
	isOccupancyOpen = false;
	isHotelsOpen = false;
	isPackagesOpen = false;
	isDatePickerOpen = false;
	selectedHotel: Partial<HotelInterface>;
	selectedPackage = model<AvailabilityInputDropdownItem>();
	nights = 1;

	arrivalDate: Date;
	departureDate: Date;
	_resetDateRange = 0;

	cn = cn;

	constructor() {
		this.availability$ = this.updateAvailability$.pipe(
			filter((request) => !!request && !!request.hotelGUID),
			distinctUntilChanged(areAvailabilityRequestsEqual),
			tap(() => (this.isLoading = true)),
			switchMap((request) =>
				this.availability.getAvailabilityDates(
					request.hotelGUID,
					request.endDate,
					request.startDate,
					request.occupancies,
					request.accommodationTypes,
					request.dealGUID,
					request.packageGUID,
					request.roomGUID,
					request.nights,
				),
			),
			tap(() => (this.isLoading = false)),
			shareReplay(1),
		);

		effect(() => {
			const currentPackage = this.packages()?.find(
				// TODO: Fix typing in VPL-31625
				(item) => (item['GUID'] ?? item.guid) === this.packageGUID(),
			);
			/* istanbul ignore else */
			if (currentPackage) {
				this.onPackageSelect(currentPackage);
			}
		});

		effect(() => {
			this.activeAccommodationType();
			this.getAvailability();
		});
	}

	ngOnInit() {
		this.nights = this.stepperConfig?.min ?? 1;

		if (this.minDate && this.minDate.toString() !== yesterday.toString()) {
			this.firstDateInCalendar = dayjs(this.minDate).format('YYYY-MM-DD');
		}

		if (
			this.initialArrivalDate &&
			this.minDate.toString() === yesterday.toString()
		) {
			this.firstDateInCalendar = dayjs(this.initialArrivalDate)
				.startOf('month')
				.format('YYYY-MM-DD');
		}

		if (!this.showOccupancy()) {
			this.occupancy = [
				{ adults: this.defaultAdults, children: 0, infants: 0 },
			];
		}

		// Validate initial dates if provided and clear if invalid
		if (this.initialArrivalDate && this.initialDepartureDate) {
			this.arrivalDate = this.initialArrivalDate;
			this.departureDate = this.initialDepartureDate;

			// Calculate nights if dates are valid
			const calculatedNights = dayjs(this.departureDate).diff(
				dayjs(this.arrivalDate),
				'days',
			);

			// Respect minimum nights constraint
			const minNights = this.stepperConfig?.min ?? 1;
			this.nights = Math.max(calculatedNights, minNights);
		}

		this.getAvailability();
	}

	onCtaEmit(event: Event, showPackages = false) {
		this.isOccupancyOpen = false;
		this.isPackagesOpen = showPackages;
		this.isDatePickerOpen = !showPackages;

		this.getAvailability();

		event.stopPropagation();
	}

	onBackButton(
		event: Event,
		isHotelsOpen: boolean,
		isOccupancyOpen: boolean,
		isPackagesOpen: boolean,
		isDatePickerOpen: boolean,
	) {
		this.isHotelsOpen = isHotelsOpen;
		this.isOccupancyOpen = isOccupancyOpen;
		this.isPackagesOpen = isPackagesOpen;
		this.isDatePickerOpen = isDatePickerOpen;

		event.stopPropagation();
	}

	onDatePickerOpened(isOpen: boolean) {
		this.isDatePickerOpen = isOpen;
	}

	onOccupancyOpenEmit(isOpen: boolean) {
		this.isOccupancyOpen = isOpen;
	}

	onOccupancyEmit(occupancy: OccupancyInterface[]) {
		this.occupancy = occupancy;
		this.occupancyChanged.emit(occupancy);
		this.getAvailability();
	}

	onHotelSelect(hotel: Partial<HotelInterface>) {
		this.selectedHotel = hotel;
		this.getAvailability();
	}

	onPackageSelect(packageItem: AvailabilityInputDropdownItem) {
		const currentPackageIndex = this.packages().findIndex(
			(item) => item['GUID'] === packageItem.GUID,
		);

		this.selectedPackage.set(packageItem);
		this.packageSelected.emit(currentPackageIndex);
		this.resetDateRange();
		this.getAvailability();
	}

	resetDateRange() {
		this._resetDateRange++;
	}

	recalculateAvailabilityForToday() {
		this.firstDateInCalendar = dayjs()
			.startOf('month')
			.format('YYYY-MM-DD');
		this.getAvailability();
	}

	onMonthChange(newFirstDate: string) {
		this.firstDateInCalendar = newFirstDate;

		this.jumpToFirstAvailableDate = false;
		this.getAvailability();
	}

	onStepperUpdated(nights: number) {
		this.nights = nights;
		this.getAvailability();
	}

	getAvailability() {
		/* istanbul ignore else */
		if (isPlatformBrowser(this.platformId)) {
			const accommodationTypes =
				this.activeAccommodationType() === 0
					? []
					: [
							this.accommodationTypes[
								this.activeAccommodationType() - 1
							].accommodationType,
						];
			const request: AvailabilityRequestInterface = {
				accommodationTypes,
				hotelGUID: this.hotelGUID
					? this.hotelGUID
					: this.selectedHotel?.guid,
				startDate: this.firstDateInCalendar,
				endDate: dayjs(this.firstDateInCalendar)
					.add(2, 'month')
					.format('YYYY-MM-DD'),
				occupancies: this.occupancy,
				dealGUID: this.dealGUID,
				packageGUID: this.showPackages()
					? (this.selectedPackage()?.['GUID'] ?? this.packageGUID())
					: undefined,
				roomGUID: this.roomGUID,
			};

			if (this.stepperConfig?.useStepper) {
				request.nights = this.nights;
			}
			this.updateAvailability$.next(request);
		}
	}

	updateModel(event) {
		this.arrivalDate = event.arrivalDate;
		this.departureDate = event.departureDate;

		this.getAvailability();
	}

	goToBookingtool() {
		if (this.useCustomEmitter) {
			this.customEmitter.emit({
				arrivalDate: this.arrivalDate,
				departureDate: this.departureDate,
				occupancy: this.occupancy,
				hotel: this.selectedHotel,
				roomGUID: this.roomGUID,
				dealGUID: this.dealGUID,
				packageGUID: this.showPackages()
					? (this.selectedPackage()?.['GUID'] ?? this.packageGUID())
					: undefined,
				accommodationType:
					this.activeAccommodationType() === 0
						? undefined
						: this.accommodationTypes[
								this.activeAccommodationType() - 1
							].accommodationType,
			});
			return;
		}

		if (this.bookingtoolUrl) {
			const languageCode = `&languageCode=${this.language}`;
			const arrivalString = this.arrivalDate
				? `&startDate=${dayjs(this.arrivalDate).format('YYYY-MM-DD')}`
				: '';
			const departureString = this.departureDate
				? `&endDate=${dayjs(this.departureDate).format('YYYY-MM-DD')}`
				: '';
			const occupancyString = this.occupancy
				? `&occupancy=${JSON.stringify(this.occupancy)}`
				: '';
			const roomString = this.roomGUID
				? `&roomGUID=${this.roomGUID}`
				: '';
			const dealString = this.dealGUID
				? `&dealGUID=${this.dealGUID}`
				: '';
			const packageString = this.showPackages()
				? `&packageGUID=` +
					(this.selectedPackage()?.['GUID'] ?? this.packageGUID())
				: '';
			const parameters = `${arrivalString}${departureString}${occupancyString}${roomString}${dealString}${packageString}`;

			const currentHotelSlug = this.selectedHotel
				? `?hotelSlug=${this.selectedHotel.slug}`
				: `?hotelSlug=${this.hotelSlug()}`;

			this.setEmittedData();

			const bookingUrl = `${this.bookingtoolUrl}${currentHotelSlug}${languageCode}${parameters}`;

			if (this.openAvailabilityInNewTab()) {
				RouteHelper.redirectToExternalUrlInNewTab(bookingUrl);
			} else {
				RouteHelper.redirectToExternalUrl(bookingUrl);
			}
		}
	}

	setEmittedData() {
		const eventType = () => {
			if (this.packageGUID()) {
				return 'packages';
			} else if (this.roomGUID) {
				return 'rooms';
			} else if (this.dealGUID) {
				return 'deals';
			} else {
				return 'default';
			}
		};
		const rooms = this.occupancy.map((occupancy, i) => ({
			item_name: `ROOM${i + 1}`,
			numAdults: occupancy.adults,
			numChildren: occupancy.children,
			numBabies: occupancy.infants,
		}));
		const dayTypeBooking =
			dayjs().day() === 0 || dayjs().day() === 6 ? 'weekend' : 'weekday';
		const dayTypeVisit =
			dayjs(this.arrivalDate).day() === 5 ||
			dayjs(this.arrivalDate).day() === 6
				? 'weekend'
				: 'weekday';

		this.actionClick.emit({
			event: 'search_availability',
			eventType: eventType(),
			data: {
				dayTypeBooking,
				dayTypeVisit,
				dealGUID: this.dealGUID,
				hotelGUID: this.hotelGUID,
				leadtime: dayjs(this.arrivalDate).diff(
					dayjs().format('YYYY-MM-DD'),
					'day',
				),
				numGuests: this.occupancy.reduce((acc, curr) => {
					return acc + curr.adults + curr.children + curr.infants;
				}, 0),
				numRooms: this.occupancy.length,
				numNights: dayjs(this.departureDate).diff(
					dayjs(this.arrivalDate),
					'days',
				),
				packageGUID:
					this.selectedPackage()?.['GUID'] ?? this.packageGUID(),
				roomGUID: this.roomGUID,
				rooms,
				selectedArrivalDate: dayjs(this.arrivalDate).format(
					'YYYY-MM-DD',
				),
				selectedDepartureDate: dayjs(this.departureDate).format(
					'YYYY-MM-DD',
				),
			},
		});
	}
}

/**
 * Compares two AvailabilityRequestInterface objects for equality,
 * used primarily for the distinctUntilChanged operator.
 * It performs a shallow comparison of most properties and a deep comparison
 * for `occupancies` and `accommodationTypes` arrays using JSON.stringify.
 * @param prev The previous AvailabilityRequestInterface object.
 * @param curr The current AvailabilityRequestInterface object.
 * @returns True if the requests are considered equal, false otherwise.
 */
export function areAvailabilityRequestsEqual(
	prev: AvailabilityRequestInterface,
	curr: AvailabilityRequestInterface,
): boolean {
	if (!prev || !curr) return false;
	const shallowEqual =
		prev.hotelGUID === curr.hotelGUID &&
		prev.startDate === curr.startDate &&
		prev.endDate === curr.endDate &&
		prev.dealGUID === curr.dealGUID &&
		prev.packageGUID === curr.packageGUID &&
		prev.roomGUID === curr.roomGUID &&
		prev.nights === curr.nights;
	const occupanciesEqual =
		JSON.stringify(prev.occupancies) === JSON.stringify(curr.occupancies);
	const accommodationTypesEqual =
		JSON.stringify(prev.accommodationTypes) ===
		JSON.stringify(curr.accommodationTypes);
	return shallowEqual && occupanciesEqual && accommodationTypesEqual;
}
